/**
 * Mirrors backend class-validator: src/utils/validators/is-bsb.validator.ts
 * and is-tfn.validator.ts — keep messages aligned with API errors.
 */

export const BSB_ERROR_MESSAGE =
  "BSB is invalid. Must be 6 digits, optionally separated by a dash or space (e.g. 123-456).";

/** Normalises any BSB-ish input to canonical "XXX-XXX" (or partial while typing). */
export function formatBSB(value) {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 6);
  return digits.length > 3
    ? `${digits.slice(0, 3)}-${digits.slice(3)}`
    : digits;
}

export const TFN_ERROR_MESSAGE =
  "Tax File Number (TFN) must be exactly 9 digits.";

/** Strip non-digits and cap at 9 characters. */
export function formatTfnDigits(value) {
  return String(value ?? "").replace(/\D/g, "").slice(0, 9);
}

/**
 * @returns {string | null} Error message or null if valid / still incomplete (no error while typing)
 */
export function getBsbError(value) {
  const v = String(value ?? "").trim();
  if (!v) return null;
  const digits = v.replace(/[\s-]/g, "");
  if (/[^\d]/.test(digits)) return BSB_ERROR_MESSAGE;
  if (digits.length < 6) return null; // still typing — no error yet
  if (digits.length !== 6) return BSB_ERROR_MESSAGE;
  return null;
}

/**
 * @returns {string | null}
 * Format-only check (9 digits). ATO checksum is not enforced on workforce entry
 * so valid staff TFNs / onboarding numbers are not blocked by a misleading error.
 */
export function getTfnError(value) {
  const digits = formatTfnDigits(value);
  if (!digits) return null;
  if (digits.length < 9) return null;
  if (digits.length !== 9) return TFN_ERROR_MESSAGE;
  return null;
}

/** For Next / Save: BSB must be present and fully valid. */
export function getBsbErrorRequired(value) {
  const v = String(value ?? "").trim();
  if (!v) return "BSB is required.";
  const digits = v.replace(/[\s-]/g, "");
  if (/[^\d]/.test(digits) || digits.length !== 6) return BSB_ERROR_MESSAGE;
  return null;
}

/** For Next / Save: TFN must be present and exactly 9 digits. */
export function getTfnErrorRequired(value) {
  const digits = formatTfnDigits(value);
  if (!digits) return "Tax File Number is required.";
  if (digits.length !== 9) return TFN_ERROR_MESSAGE;
  return null;
}

/** Strip non-digits; no fixed length (e.g. super fund member number). */
export function formatDigitsOnly(value) {
  return String(value ?? "").replace(/\D/g, "");
}

export const ABN_ERROR_MESSAGE = "ABN must be exactly 11 digits.";

/** Strip non-digits and cap at 11 characters. */
export function formatAbnDigits(value) {
  return String(value ?? "").replace(/\D/g, "").slice(0, 11);
}

export function getAbnError(value) {
  const digits = formatAbnDigits(value);
  if (!digits) return null;
  if (digits.length < 11) return null;
  if (digits.length !== 11) return ABN_ERROR_MESSAGE;
  return null;
}

export function getAbnErrorRequired(value) {
  const digits = formatAbnDigits(value);
  if (!digits) return "ABN is required.";
  if (digits.length !== 11) return ABN_ERROR_MESSAGE;
  return null;
}
